/**
 * PROJECT JURISDICTION (2026-10-07, Matt): which city's rules a project's drawings follow.
 * Newport Beach requirements (the height datum, its section/elevation limit lines and its
 * A001 calculations) show only on Newport Beach projects; the Laguna Beach site development
 * items (average lot depth/width lines, Lot Dimensions and Lot Slope notes) only on Laguna
 * Beach projects.
 *
 * Source of truth: the project's PARCEL research — the zone's jurisdiction (its zone name
 * reads "Newport Beach - R-1-6000 Zone"), falling back to the parcel's project city.
 * Mirrored into the doc by ModelerPage alongside the Site Plan Notes (the same
 * mirror-at-source pattern as projectInfo.ts), so sheets render synchronously and offline.
 * Until it is mirrored, the project's own city (projectInfo.cityStateZip) stands in.
 */
import { maps, transact, type ModelDoc } from './doc'
import { getProjectInfo } from './projectInfo'

const KEY = 'siteJurisdiction'

/** the mirrored jurisdiction name ('' = never mirrored) */
export function getSiteJurisdiction(model: ModelDoc): string {
  const v = maps(model).settings.get(KEY)
  return typeof v === 'string' ? v : ''
}

export function setSiteJurisdiction(model: ModelDoc, name: string): void {
  transact(model, () => {
    maps(model).settings.set(KEY, String(name || '').trim())
  })
}

/** the jurisdiction the drawings follow: the parcel's, else the project's city */
export function projectJurisdiction(model: ModelDoc): string {
  const j = getSiteJurisdiction(model)
  if (j) return j
  const csz = getProjectInfo(model).cityStateZip || ''
  return csz.split(',')[0].trim()
}

const norm = (s: string) => s.toLowerCase().replace(/\s+/g, ' ').trim()
export const isNewportBeach = (model: ModelDoc) => norm(projectJurisdiction(model)).startsWith('newport beach')
export const isLagunaBeach = (model: ModelDoc) => norm(projectJurisdiction(model)).startsWith('laguna beach')
